import {useEffect, useId, useState, type ReactNode} from 'react';
import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import {Mic, Square, Volume2} from 'lucide-react';
import {useAppDispatch} from '../../store/store';
import {
    getAutoGainControl,
    getSelectedMicrophone,
    getSelectedSpeaker,
    setAutoGainControl,
    setSelectedMicrophoneDevice,
    setSelectedSpeakerDevice,
} from '../../store/features/calls/dialerSlice';
import {buildAudioConstraints} from '../../components/Dialer/hooks/audioConstraints';
import {resolveActiveDevice} from '../../components/Dialer/hooks/audioDevices';
import logger from '../../services/logger';
import {useAudioDevices} from './useAudioDevices';
import {audioSettingsMessages as m} from './audioSettingsMessages';

const TONE_HZ = 440;
const TONE_MS = 700;
const GOOD_LEVEL = 0.08;
const LOW_LEVEL = 0.01;

type SinkAudio = HTMLAudioElement & {setSinkId?: (deviceId: string) => Promise<void>};

const playTestTone = async (deviceId: string | undefined): Promise<void> => {
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const destination = context.createMediaStreamDestination();
    oscillator.frequency.value = TONE_HZ;
    gain.gain.value = 0.2;
    oscillator.connect(gain).connect(destination);
    const element = new Audio() as SinkAudio;
    element.srcObject = destination.stream;
    if (deviceId && element.setSinkId) {
        await element.setSinkId(deviceId);
    }
    oscillator.start();
    await element.play();
    window.setTimeout(() => {
        oscillator.stop();
        element.pause();
        element.srcObject = null;
        void context.close();
    }, TONE_MS);
};

const useMicrophoneLevel = (deviceId: string | undefined, autoGainControl: boolean, active: boolean): number => {
    const [level, setLevel] = useState(0);
    useEffect(() => {
        if (!active) {
            setLevel(0);
            return;
        }
        let frame = 0;
        let stream: MediaStream | undefined;
        let context: AudioContext | undefined;
        let cancelled = false;
        void navigator.mediaDevices.getUserMedia({audio: buildAudioConstraints(deviceId, autoGainControl)}).then(media => {
            if (cancelled) {
                media.getTracks().forEach(track => track.stop());
                return;
            }
            stream = media;
            context = new AudioContext();
            void context.resume();
            const analyser = context.createAnalyser();
            analyser.fftSize = 512;
            context.createMediaStreamSource(media).connect(analyser);
            const samples = new Float32Array(analyser.fftSize);
            const tick = () => {
                analyser.getFloatTimeDomainData(samples);
                const rms = Math.sqrt(samples.reduce((sum, value) => sum + value * value, 0) / samples.length);
                setLevel(current => Math.max(rms, current * 0.85));
                frame = requestAnimationFrame(tick);
            };
            tick();
        }).catch(error => logger.warn('Microphone test failed', {
            source: 'audio_settings',
            event: 'microphone_test_failed',
            errorName: (error as Error)?.name,
        }));
        return () => {
            cancelled = true;
            cancelAnimationFrame(frame);
            stream?.getTracks().forEach(track => track.stop());
            void context?.close();
        };
    }, [active, autoGainControl, deviceId]);
    return level;
};

const Card = ({title, hint, children}: {title: string; hint: string; children: ReactNode}) => (
    <section className="flex flex-col gap-4 rounded-2xl border border-[var(--phq-call-line)] bg-[var(--phq-call-surface)] p-5">
        <header>
            <h3 className="text-base font-semibold text-[var(--phq-call-ink)]">{title}</h3>
            <p className="text-sm text-[var(--phq-call-ink-muted)]">{hint}</p>
        </header>
        {children}
    </section>
);

const DeviceSelect = ({label, devices, value, fallbackLabel, testId, onChange}: {
    label: string;
    devices: readonly MediaDeviceInfo[];
    value: string | undefined;
    fallbackLabel: string;
    testId: string;
    onChange: (deviceId: string) => void;
}) => {
    const id = useId();
    return (
        <div className="flex flex-col gap-1.5">
            <label htmlFor={id} className="text-sm font-medium text-[var(--phq-call-ink)]">{label}</label>
            <select
                id={id}
                data-testid={testId}
                value={value ?? ''}
                onChange={event => onChange(event.target.value)}
                className="h-10 rounded-[10px] border border-[var(--phq-call-line)] bg-[var(--phq-call-surface)] px-3 text-sm text-[var(--phq-call-ink)] focus:outline-none focus:ring-2 focus:ring-[var(--phq-call-brand-focus)]"
            >
                {devices.length === 0 && <option value="">{fallbackLabel}</option>}
                {devices.map(device => <option key={device.deviceId} value={device.deviceId}>{device.label || fallbackLabel}</option>)}
            </select>
        </div>
    );
};

const SecondaryButton = ({icon, label, testId, onClick}: {icon: ReactNode; label: string; testId: string; onClick: () => void}) => (
    <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className="phq-call-press inline-flex h-9 w-fit items-center gap-2 rounded-[10px] border border-solid border-[var(--phq-call-line)] px-3 text-sm font-semibold text-[var(--phq-call-ink)] hover:bg-[var(--phq-call-surface-muted)]"
    >
        {icon}
        {label}
    </button>
);

export const AudioSettingsSection = () => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const {inputs, outputs, blocked} = useAudioDevices();
    const selectedMicrophone = useSelector(getSelectedMicrophone);
    const selectedSpeaker = useSelector(getSelectedSpeaker);
    const autoGainControl = useSelector(getAutoGainControl);
    const [testing, setTesting] = useState(false);
    const microphone = resolveActiveDevice([...inputs], selectedMicrophone);
    const speaker = resolveActiveDevice([...outputs], selectedSpeaker);
    const level = useMicrophoneLevel(microphone, autoGainControl, testing);
    const levelLabel = level >= GOOD_LEVEL ? m.levelGood : level >= LOW_LEVEL ? m.levelLow : m.levelNone;
    const switchId = useId();

    return (
        <div className="phq-call-light flex w-full max-w-[640px] flex-col gap-4 px-8 py-6" data-testid="audio-settings">
            <Card title={intl.formatMessage(m.microphone)} hint={intl.formatMessage(m.microphoneHint)}>
                {blocked && <p role="status" className="text-sm font-medium text-[var(--phq-call-danger)]">{intl.formatMessage(m.microphoneBlocked)}</p>}
                <DeviceSelect
                    label={intl.formatMessage(m.inputDevice)}
                    devices={inputs}
                    value={microphone}
                    fallbackLabel={intl.formatMessage(m.systemDefault)}
                    testId="audio-settings-microphone"
                    onChange={deviceId => dispatch(setSelectedMicrophoneDevice(deviceId))}
                />
                {testing && (
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-[var(--phq-call-ink)]">{intl.formatMessage(m.inputLevel)}</span>
                        <div
                            role="meter"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={Math.round(Math.min(1, level * 5) * 100)}
                            aria-label={intl.formatMessage(m.inputLevel)}
                            className="h-2 flex-1 overflow-hidden rounded-full bg-[var(--phq-call-surface-muted)]"
                        >
                            <div className="h-full rounded-full bg-[var(--phq-call-accept)] transition-[width] duration-75" style={{width: `${Math.min(1, level * 5) * 100}%`}}/>
                        </div>
                        <span className="w-20 whitespace-nowrap text-right text-sm font-semibold text-[var(--phq-call-ink-muted)]">{intl.formatMessage(levelLabel)}</span>
                    </div>
                )}
                <SecondaryButton
                    icon={testing ? <Square size={16} aria-hidden="true"/> : <Mic size={16} aria-hidden="true"/>}
                    label={intl.formatMessage(testing ? m.stopTest : m.testMicrophone)}
                    testId="audio-settings-test-microphone"
                    onClick={() => setTesting(current => !current)}
                />
                <div className="flex items-start justify-between gap-4 border-t border-[var(--phq-call-line)] pt-4">
                    <label htmlFor={switchId} className="flex cursor-pointer flex-col">
                        <span className="text-sm font-medium text-[var(--phq-call-ink)]">{intl.formatMessage(m.autoGain)}</span>
                        <span className="text-sm text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.autoGainHint)}</span>
                    </label>
                    <button
                        id={switchId}
                        type="button"
                        role="switch"
                        aria-checked={autoGainControl}
                        data-testid="audio-settings-auto-gain"
                        onClick={() => dispatch(setAutoGainControl(!autoGainControl))}
                        className={`relative mt-1 inline-flex h-6 w-10 shrink-0 cursor-pointer items-center rounded-full px-0.5 transition-colors ${autoGainControl ? 'bg-[var(--phq-call-brand)]' : 'bg-[var(--phq-call-line)]'}`}
                    >
                        <span aria-hidden="true" className={`h-5 w-5 rounded-full bg-surface shadow-[0_1px_2px_rgba(0,0,0,0.25)] transition-transform ${autoGainControl ? 'translate-x-4' : ''}`}/>
                    </button>
                </div>
            </Card>
            <Card title={intl.formatMessage(m.speaker)} hint={intl.formatMessage(m.speakerHint)}>
                <DeviceSelect
                    label={intl.formatMessage(m.outputDevice)}
                    devices={outputs}
                    value={speaker}
                    fallbackLabel={intl.formatMessage(m.systemDefault)}
                    testId="audio-settings-speaker"
                    onChange={deviceId => dispatch(setSelectedSpeakerDevice(deviceId))}
                />
                <div className="flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <span className="text-sm font-medium text-[var(--phq-call-ink)]">{intl.formatMessage(m.test)}</span>
                        <span className="text-sm text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.testHint)}</span>
                    </div>
                    <SecondaryButton
                        icon={<Volume2 size={16} aria-hidden="true"/>}
                        label={intl.formatMessage(m.playTestSound)}
                        testId="audio-settings-test-speaker"
                        onClick={() => void playTestTone(speaker).catch(error => logger.warn('Speaker test failed', {
                            source: 'audio_settings',
                            event: 'speaker_test_failed',
                            errorName: (error as Error)?.name,
                        }))}
                    />
                </div>
            </Card>
            <p className="text-sm text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.inCallHint)}</p>
        </div>
    );
};
